import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';

const INFRA_REFRESH_MS = 15_000;
const HEALTH_REFRESH_MS = 30_000;
const DEFAULT_CADENCE_MINUTES = 15;

type Severity = 'critical' | 'warning' | 'healthy';
type RangeKey = '15m' | '1h' | '6h' | '24h';
type MetricKey = 'scheduler' | 'database' | 'workers';

type Incident = {
  id: string;
  severity: Exclude<Severity, 'healthy'>;
  title: string;
  signal: string;
  action: string;
};

const METRICS: Record<MetricKey, { label: string; key: string; unit: string; color: string; description: string }> = {
  scheduler: {
    label: 'Scheduler',
    key: 'schedulerLoadPct',
    unit: '% of cores',
    color: '#5cf4ca',
    description: 'One-minute host load normalized by scheduler CPU cores',
  },
  database: {
    label: 'Database',
    key: 'databaseActivePct',
    unit: '% max connections',
    color: '#79a8ff',
    description: 'Active PostgreSQL sessions as a share of max connections',
  },
  workers: {
    label: 'Workers',
    key: 'workerAvgActiveJobs',
    unit: 'jobs / worker',
    color: '#c38cff',
    description: 'Active sync jobs divided by distinct connected worker hosts',
  },
};

const number = (value: unknown) => Number(value ?? 0);

function compact(value: number) {
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

function duration(seconds: number | null | undefined) {
  if (seconds == null || !Number.isFinite(Number(seconds))) return '—';
  const value = Number(seconds);
  if (value < 60) return `${Math.round(value)}s`;
  if (value < 3600) return `${Math.floor(value / 60)}m ${Math.round(value % 60)}s`;
  return `${Math.floor(value / 3600)}h ${Math.round((value % 3600) / 60)}m`;
}

function severityRank(severity: Severity) {
  return severity === 'critical' ? 2 : severity === 'warning' ? 1 : 0;
}

function configuredIntervalMinutes(health: any, platformId: string) {
  const intervals = (health?.cadenceByPlatformInterval || [])
    .filter((row: any) => row.platform_id === platformId)
    .map((row: any) => number(row.sync_interval_minutes))
    .filter((value: number) => value > 0);
  return intervals.length ? Math.min(...intervals) : DEFAULT_CADENCE_MINUTES;
}

function platformSeverity(row: any): Severity {
  const completed = number(row.completed_1h);
  const failed = number(row.failed_1h);
  const failureRate = completed ? failed / completed : 0;
  const terminalP95 = number(row.due_to_terminal_p95_secs);
  const targetSeconds = number(row.sync_interval_minutes) * 60;
  if (terminalP95 > targetSeconds * 1.25 || (completed >= 50 && failed >= 20 && failureRate >= 0.2)) return 'critical';
  if (terminalP95 > targetSeconds * 0.8 || (failed >= 3 && failureRate >= 0.05)) return 'warning';
  return 'healthy';
}

function buildIncidents(health: any, queue: any, infra: any): Incident[] {
  const incidents: Incident[] = [];
  const scheduler = infra?.snapshot?.scheduler || {};
  const database = infra?.snapshot?.database || {};
  const archive = health?.archiveHealth || {};

  if (number(scheduler.normalizedLoad1Pct) >= 100) {
    incidents.push({
      id: 'scheduler-load',
      severity: 'critical',
      title: 'Scheduler host load exceeds available cores',
      signal: `${number(scheduler.normalizedLoad1Pct).toFixed(0)}% normalized 1m load · ${number(scheduler.load5).toFixed(1)} five-minute load`,
      action: 'Inspect active PostgreSQL queries and scheduler logs before scaling. Terminate only a precisely identified runaway diagnostic; do not restart the evidence pipeline blindly.',
    });
  } else if (number(scheduler.normalizedLoad1Pct) >= 75) {
    incidents.push({
      id: 'scheduler-load',
      severity: 'warning',
      title: 'Scheduler host has limited compute headroom',
      signal: `${number(scheduler.normalizedLoad1Pct).toFixed(0)}% normalized 1m load`,
      action: 'Correlate database waits, query age, disk pressure, and queue admission before deciding whether the host needs more capacity.',
    });
  }

  if (number(database.waiting) >= 10 || number(database.activePctOfMax) >= 70) {
    incidents.push({
      id: 'database-pressure',
      severity: 'critical',
      title: 'PostgreSQL is under material pressure',
      signal: `${number(database.active)} active · ${number(database.waiting)} waiting · ${number(database.activePctOfMax).toFixed(0)}% of connection ceiling`,
      action: 'List the oldest active and waiting queries with their application names. Separate a runaway diagnostic from normal worker demand before changing database size or pool limits.',
    });
  } else if (number(database.waiting) > 0 || number(database.activePctOfMax) >= 40) {
    incidents.push({
      id: 'database-pressure',
      severity: 'warning',
      title: 'PostgreSQL pressure is elevated',
      signal: `${number(database.active)} active · ${number(database.waiting)} waiting`,
      action: 'Watch the next samples and inspect query age if waits persist. A short admission burst alone is not a scaling signal.',
    });
  }

  const freshDebt = number(archive.fresh);
  if (number(archive.failed) > 0 || number(archive.backed_off) > 0) {
    incidents.push({
      id: 'archive-retry',
      severity: 'critical',
      title: 'Archive verification is failing',
      signal: `${number(archive.failed)} failed · ${number(archive.backed_off)} backed off`,
      action: 'Inspect the oldest failed claim and durable retry clock. Require positive-size S3 HEAD proof before any source-row purge.',
    });
  } else if (freshDebt > 1000) {
    incidents.push({
      id: 'archive-debt',
      severity: 'critical',
      title: 'Fresh archive debt is above the safety threshold',
      signal: `${freshDebt.toLocaleString()} terminal jobs await verified archives`,
      action: 'Compare archive-worker throughput with terminal throughput, then inspect claim age, disk pressure, and S3 verification failures.',
    });
  } else if (freshDebt > 50) {
    incidents.push({
      id: 'archive-debt',
      severity: 'warning',
      title: 'Archive debt is building',
      signal: `${freshDebt.toLocaleString()} fresh jobs await proof`,
      action: 'Watch verified archive throughput and the oldest claim before the debt reaches the scheduler throttle.',
    });
  }

  (health?.lagByPlatform || []).forEach((rawRow: any) => {
    const row = {
      ...rawRow,
      sync_interval_minutes: configuredIntervalMinutes(health, rawRow.platform_id),
    };
    const terminalP95 = number(row.due_to_terminal_p95_secs);
    const targetSeconds = number(row.sync_interval_minutes) * 60;
    const completed = number(row.completed_1h);
    const failed = number(row.failed_1h);
    const failureRate = completed ? failed / completed : 0;
    if (terminalP95 > targetSeconds * 1.25) {
      incidents.push({
        id: `cadence-${row.platform_id}`,
        severity: 'critical',
        title: `${row.platform_id} is missing its ${duration(targetSeconds)} objective`,
        signal: `terminal p95 ${duration(terminalP95)} · queue p95 ${duration(number(row.queue_lag_p95_secs))}`,
        action: 'Split queue wait from provider run time and inspect the oldest active jobs. Do not change tuned provider pacing from this signal alone.',
      });
    } else if (terminalP95 > targetSeconds) {
      incidents.push({
        id: `cadence-${row.platform_id}`,
        severity: 'warning',
        title: `${row.platform_id} is just outside its ${duration(targetSeconds)} objective`,
        signal: `terminal p95 ${duration(terminalP95)} · ${(terminalP95 / targetSeconds).toFixed(2)}× target`,
        action: 'Watch the next complete window and split queue delay from provider run time before acting.',
      });
    } else if (terminalP95 > targetSeconds * 0.8) {
      incidents.push({
        id: `cadence-${row.platform_id}`,
        severity: 'warning',
        title: `${row.platform_id} has limited cadence headroom`,
        signal: `terminal p95 ${duration(terminalP95)}`,
        action: 'Watch the next complete window and split queue delay from provider run time before acting.',
      });
    }
    if (failed >= 3 && failureRate >= 0.05) {
      incidents.push({
        id: `failures-${row.platform_id}`,
        severity: completed >= 50 && failed >= 20 && failureRate >= 0.2 ? 'critical' : 'warning',
        title: `${row.platform_id} has a concentrated failure wave`,
        signal: `${failed}/${completed} jobs failed in 1h · ${(failureRate * 100).toFixed(1)}%`,
        action: 'Group preserved failures by HTTP versus transport cause and inspect captured response evidence before retrying.',
      });
    }
  });

  const dbQueued = number(queue?.dbQueued ?? health?.jobStates?.queued);
  const visible = number(queue?.available);
  if (dbQueued > 1000) {
    incidents.push({
      id: 'queue-pressure',
      severity: 'critical',
      title: 'The durable database queue is materially deep',
      signal: `${dbQueued.toLocaleString()} database-queued · ${visible.toLocaleString()} visible in SQS`,
      action: 'Compare database and SQS oldest age, then verify worker count and exact image digest. Depth without age is not enough to scale.',
    });
  } else if (visible > 1000) {
    incidents.push({
      id: 'queue-pressure',
      severity: 'warning',
      title: 'SQS visible depth is high; delay is not yet proven',
      signal: `${visible.toLocaleString()} visible in SQS · ${dbQueued.toLocaleString()} durable database-queued · oldest age unavailable here`,
      action: 'Check CloudWatch oldest-message age and duplicate-envelope cleanup before scaling. SQS approximate depth alone is not a delay measurement.',
    });
  } else if (dbQueued > 250 || visible > 250) {
    incidents.push({
      id: 'queue-pressure',
      severity: 'warning',
      title: 'Queue pressure is elevated',
      signal: `${dbQueued.toLocaleString()} database-queued · ${visible.toLocaleString()} visible in SQS`,
      action: 'Watch queue age and provider p95 lag together. Confirm the scheduler admission burst has ended before scaling workers.',
    });
  }

  return incidents.sort((a, b) => severityRank(b.severity) - severityRank(a.severity));
}

function SnapshotCard({ label, value, signal, tone, detail }: { label: string; value: string; signal: string; tone: Severity; detail: string }) {
  return (
    <article className={`ops-snapshot ops-tone-${tone}`}>
      <div className="ops-snapshot-top"><span>{label}</span><i /></div>
      <strong>{value}</strong>
      <div className="ops-snapshot-signal">{signal}</div>
      <p>{detail}</p>
    </article>
  );
}

export default function OpsWallboard() {
  const { logout } = useAuth();
  const [health, setHealth] = useState<any>(null);
  const [queue, setQueue] = useState<any>(null);
  const [infra, setInfra] = useState<any>(null);
  const [range, setRange] = useState<RangeKey>('1h');
  const [metric, setMetric] = useState<MetricKey>('scheduler');
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [sampleLatency, setSampleLatency] = useState<number | null>(null);
  const [now, setNow] = useState(new Date());
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [agentOpen, setAgentOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const infraInFlight = useRef(false);
  const healthInFlight = useRef(false);

  const refreshInfra = useCallback(async (selectedRange: RangeKey) => {
    if (infraInFlight.current) return;
    infraInFlight.current = true;
    const startedAt = performance.now();
    try {
      const result = await api.getInfraHealth(selectedRange);
      setInfra(result);
      setSampleLatency(Math.round(performance.now() - startedAt));
      setLastRefresh(new Date());
      setError('');
    } catch (err: any) {
      setError(err?.message || 'Infrastructure telemetry is unavailable');
    } finally {
      infraInFlight.current = false;
    }
  }, []);

  const refreshHealth = useCallback(async () => {
    if (healthInFlight.current) return;
    healthInFlight.current = true;
    try {
      const [healthResult, queueResult] = await Promise.all([
        api.getSystemHealth(),
        api.getQueueStats().catch(() => null),
      ]);
      setHealth(healthResult);
      setQueue(queueResult);
    } catch (err: any) {
      setError(err?.message || 'Pipeline telemetry is unavailable');
    } finally {
      healthInFlight.current = false;
    }
  }, []);

  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    await refreshInfra(range);
    await refreshHealth();
    setRefreshing(false);
  }, [range, refreshHealth, refreshInfra]);

  useEffect(() => {
    refreshInfra(range);
  }, [range, refreshInfra]);

  useEffect(() => {
    let healthTimer: number | undefined;
    const healthKickoff = window.setTimeout(() => {
      refreshHealth();
      healthTimer = window.setInterval(refreshHealth, HEALTH_REFRESH_MS);
    }, INFRA_REFRESH_MS / 2);
    const infraTimer = window.setInterval(() => refreshInfra(range), INFRA_REFRESH_MS);
    const clockTimer = window.setInterval(() => setNow(new Date()), 1_000);
    return () => {
      window.clearTimeout(healthKickoff);
      window.clearInterval(infraTimer);
      if (healthTimer != null) window.clearInterval(healthTimer);
      window.clearInterval(clockTimer);
    };
  }, [range, refreshHealth, refreshInfra]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'a' && !event.metaKey && !event.ctrlKey) setAgentOpen((open) => !open);
      if (event.key.toLowerCase() === 'f' && !event.metaKey && !event.ctrlKey) {
        if (document.fullscreenElement) document.exitFullscreen();
        else document.documentElement.requestFullscreen();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const incidents = useMemo(() => buildIncidents(health, queue, infra), [health, queue, infra]);
  const overall: Severity = incidents.some((incident) => incident.severity === 'critical')
    ? 'critical'
    : incidents.length ? 'warning' : 'healthy';
  const snapshot = infra?.snapshot || {};
  const scheduler = snapshot.scheduler || {};
  const database = snapshot.database || {};
  const workers = snapshot.workers || {};
  const stale = !lastRefresh || now.getTime() - lastRefresh.getTime() > INFRA_REFRESH_MS * 3;
  const archiveDebt = number(health?.archiveHealth?.pending) + number(health?.archiveHealth?.archiving) + number(health?.archiveHealth?.failed);

  const throughputRows = health?.throughput || [];
  const totalPerMinute = throughputRows.reduce((sum: number, row: any) => sum + number(row.per_min), 0);
  const terminalP95 = Math.max(0, ...(health?.lagByPlatform || []).map((row: any) => number(row.due_to_terminal_p95_secs)));

  const platformRows = useMemo(() => {
    const throughputMap = new Map(throughputRows.map((row: any) => [row.platform_id, row]));
    const pendingMap = new Map((health?.pendingByType || []).map((row: any) => [row.platform_id, number(row.waiting)]));
    return (health?.lagByPlatform || []).map((row: any) => ({
      ...row,
      sync_interval_minutes: configuredIntervalMinutes(health, row.platform_id),
      per_min: number((throughputMap.get(row.platform_id) as any)?.per_min),
      waiting: pendingMap.get(row.platform_id) || 0,
    })).map((row: any) => ({
      ...row,
      severity: platformSeverity(row),
    })).sort((a: any, b: any) => severityRank(b.severity) - severityRank(a.severity) || number(b.due_to_terminal_p95_secs) - number(a.due_to_terminal_p95_secs));
  }, [health, throughputRows]);

  const worstCadence = platformRows.reduce((worst: any, row: any) => {
    const targetSeconds = number(row.sync_interval_minutes) * 60;
    const ratio = targetSeconds > 0 ? number(row.due_to_terminal_p95_secs) / targetSeconds : 0;
    return ratio > worst.ratio ? { platform: row.platform_id, ratio } : worst;
  }, { platform: '—', ratio: 0 });

  const chartMetric = METRICS[metric];
  const chartData = useMemo(() => (infra?.history || []).map((point: any) => ({
    ...point,
    label: new Date(point.observedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  })), [infra]);
  const chartValues = chartData.map((point: any) => number(point[chartMetric.key]));
  const chartCurrent = chartValues.at(-1) || 0;
  const chartPeak = chartValues.length ? Math.max(...chartValues) : 0;
  const chartAverage = chartValues.length ? chartValues.reduce((sum: number, value: number) => sum + value, 0) / chartValues.length : 0;

  const agentBrief = useMemo(() => ({
    schemaVersion: 'ops-diagnostics.v2',
    status: overall,
    observedAt: snapshot.observedAt || health?.queriedAt || null,
    dataFresh: !stale,
    objective: 'Maintain durable provider-native evidence at each target\'s configured cadence.',
    infrastructure: {
      scheduler: {
        normalizedLoad1Pct: number(scheduler.normalizedLoad1Pct),
        load1: number(scheduler.load1),
        load5: number(scheduler.load5),
        load15: number(scheduler.load15),
        cpuCores: number(scheduler.cpuCount),
        diskUsedPct: scheduler.disk?.usedPct ?? null,
      },
      database: {
        connections: number(database.connections),
        active: number(database.active),
        waiting: number(database.waiting),
        longRunningOver15s: number(database.longRunning),
        maxConnections: number(database.maxConnections),
      },
      workers: {
        workersConnected: number(workers.connected),
        activeJobs: number(workers.activeJobs),
        averageActiveJobs: number(workers.avgActiveJobs),
        maxActiveDatabaseSessions: number(workers.maxActiveDbSessions),
        loadDefinition: workers.loadDefinition || 'active sync jobs divided by connected worker hosts; not host CPU',
      },
    },
    pipeline: {
      completedPerMinute5m: Number(totalPerMinute.toFixed(1)),
      worstDueToTerminalP95Seconds1h: terminalP95,
      dbQueuedJobs: number(queue?.dbQueued ?? health?.jobStates?.queued),
      sqsVisibleJobs: queue?.available ?? null,
      freshArchiveDebt: number(health?.archiveHealth?.fresh),
      legacyArchiveDebt: number(health?.archiveHealth?.legacy_repair),
      archiveFailures: number(health?.archiveHealth?.failed),
    },
    incidents,
    platformSignals: platformRows.map((row: any) => ({
      platform: row.platform_id,
      status: row.severity,
      completedJobs1h: number(row.completed_1h),
      failedJobs1h: number(row.failed_1h),
      queueP95Seconds1h: number(row.queue_lag_p95_secs),
      dueToTerminalP95Seconds1h: number(row.due_to_terminal_p95_secs),
      targetIntervalMinutes: number(row.sync_interval_minutes),
      dueToTerminalP95TargetRatio: Number((number(row.due_to_terminal_p95_secs) / (number(row.sync_interval_minutes) * 60)).toFixed(3)),
      waitingOutboundRequests: row.waiting,
    })),
    cadenceObjectives24h: (health?.cadenceByPlatformInterval || []).map((row: any) => ({
      platform: row.platform_id,
      targetIntervalMinutes: number(row.sync_interval_minutes),
      completedJobs24h: number(row.completed_24h),
      dueToTerminalP99Seconds24h: number(row.due_to_terminal_p99_secs),
      p99WithinTarget: row.p99_within_interval === true || row.p99_within_interval === 'true',
    })),
    incidentPolicy: {
      cadence: {
        critical: 'rolling 1h terminal p95 > 1.25x configured interval',
        warning: 'rolling 1h terminal p95 > configured interval, or > 0.8x as limited headroom',
      },
      failureWave: {
        critical: 'rolling 1h completed >= 50, failed >= 20, and failure rate >= 20%',
        warning: 'rolling 1h failed >= 3 and failure rate >= 5%',
      },
      queue: {
        critical: 'durable database-queued jobs > 1,000',
        warning: 'durable database-queued jobs > 250, or approximate SQS visible depth > 250; SQS-only depth cannot be critical without age evidence',
      },
    },
    telemetryContract: {
      infraRefreshSeconds: INFRA_REFRESH_MS / 1000,
      pipelineRefreshSeconds: HEALTH_REFRESH_MS / 1000,
      overlappingRequestsPrevented: true,
      historyScope: infra?.historyScope || null,
      sources: ['/api/infra-health', '/api/system-health', '/api/queue-stats'],
    },
    guardrails: [
      'Diagnose from database evidence and archive proof before queue depth.',
      'Do not change provider pacing, cadence, or capture frontier from this dashboard alone.',
      'Do not purge source rows without positive-size S3 HEAD proof recorded against the source job.',
      'Preserve retry lineage and every physical attempt; never edit evidence to make a signal green.',
    ],
  }), [database, health, incidents, infra, overall, platformRows, queue, scheduler, snapshot.observedAt, stale, terminalP95, totalPerMinute, workers]);

  const copyBrief = async () => {
    await navigator.clipboard.writeText(JSON.stringify(agentBrief, null, 2));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="ops-shell" data-agent-status={overall}>
      <div className="ops-grid-field" />
      <div className="ops-orbit ops-orbit-a" />
      <div className="ops-orbit ops-orbit-b" />

      <header className="ops-header">
        <div className="ops-brand">
          <div className="ops-sigil"><span /><i /></div>
          <div><small>Niche Data / Production</small><h1>Operations cockpit</h1></div>
        </div>
        <div className="ops-telemetry-state">
          <span className={`ops-live-dot ${stale || error ? 'is-stale' : ''}`} />
          <strong>{error ? 'DEGRADED' : stale ? 'STALE' : 'LIVE'}</strong>
          <span>{lastRefresh ? lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : 'connecting'}</span>
          {sampleLatency != null && <span>{sampleLatency}ms</span>}
        </div>
        <div className="ops-clock"><strong>{now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</strong><span>{now.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}</span></div>
      </header>

      <main className="ops-main">
        <section className="ops-command-strip">
          <div className="ops-command-status">
            <div className={`ops-status-pill ops-status-${overall}`}><i />{overall === 'critical' ? 'Action required' : overall === 'warning' ? 'Watch closely' : 'Nominal'}</div>
            <p>{incidents[0]?.title || 'All observed evidence lanes are inside the current operating envelope.'}</p>
          </div>
          <div className="ops-actions">
            <button onClick={() => setAgentOpen(true)}>Agent brief <kbd>A</kbd></button>
            <button onClick={refreshAll} disabled={refreshing}>{refreshing ? 'Sampling…' : 'Sample now'}</button>
            <button onClick={() => document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen()}>Full screen <kbd>F</kbd></button>
          </div>
        </section>

        {error && <div className="ops-error">{error}. Last successful evidence remains visible.</div>}

        <section className="ops-snapshots" aria-label="Active infrastructure health snapshots">
          <SnapshotCard
            label="Scheduler host"
            value={`${number(scheduler.normalizedLoad1Pct).toFixed(0)}%`}
            signal={`LOAD ${number(scheduler.load1).toFixed(1)} / ${number(scheduler.load5).toFixed(1)} / ${number(scheduler.load15).toFixed(1)}`}
            tone={number(scheduler.normalizedLoad1Pct) >= 100 ? 'critical' : number(scheduler.normalizedLoad1Pct) >= 75 ? 'warning' : 'healthy'}
            detail={`${number(scheduler.cpuCount)} cores · ${scheduler.disk?.usedPct ?? '—'}% disk used`}
          />
          <SnapshotCard
            label="PostgreSQL"
            value={`${number(database.active)} active`}
            signal={`${number(database.waiting)} WAITING · ${number(database.longRunning)} LONG >15S`}
            tone={number(database.waiting) >= 10 || number(database.activePctOfMax) >= 70 ? 'critical' : number(database.waiting) > 0 || number(database.activePctOfMax) >= 40 ? 'warning' : 'healthy'}
            detail={`${number(database.connections)} / ${number(database.maxConnections)} connections`}
          />
          <SnapshotCard
            label="Worker fleet"
            value={`${number(workers.avgActiveJobs).toFixed(1)} avg`}
            signal={`${number(workers.activeJobs)} ACTIVE JOBS · ${number(workers.connected)} CONNECTED`}
            tone="healthy"
            detail="estimated active jobs per connected worker host"
          />
          <SnapshotCard
            label="Evidence archive"
            value={compact(archiveDebt)}
            signal={`${compact(number(health?.archiveHealth?.fresh))} FRESH · ${compact(number(health?.archiveHealth?.legacy_repair))} LEGACY`}
            tone={number(health?.archiveHealth?.failed) > 0 || archiveDebt > 1000 ? 'critical' : archiveDebt > 50 ? 'warning' : 'healthy'}
            detail={`${number(health?.archiveHealth?.failed)} failed · ${number(health?.archiveHealth?.backed_off)} backed off`}
          />
        </section>

        <section className="ops-core-grid">
          <article className="ops-panel ops-load-panel">
            <div className="ops-panel-heading ops-chart-heading">
              <div><span className="ops-kicker">Infrastructure telemetry</span><h2>Load horizon</h2><p>{chartMetric.description}</p></div>
              <div className="ops-chart-stats"><span>NOW <strong>{chartCurrent.toFixed(1)}</strong></span><span>AVG <strong>{chartAverage.toFixed(1)}</strong></span><span>PEAK <strong>{chartPeak.toFixed(1)}</strong></span></div>
            </div>
            <div className="ops-chart-controls">
              <div className="ops-segmented" aria-label="Infrastructure metric">
                {(Object.keys(METRICS) as MetricKey[]).map((key) => <button key={key} className={metric === key ? 'is-active' : ''} onClick={() => setMetric(key)}>{METRICS[key].label}</button>)}
              </div>
              <div className="ops-segmented ops-range" aria-label="Chart time range">
                {(['15m', '1h', '6h', '24h'] as RangeKey[]).map((key) => <button key={key} className={range === key ? 'is-active' : ''} onClick={() => setRange(key)}>{key}</button>)}
              </div>
            </div>
            <div className="ops-load-chart">
              {chartData.length > 1 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                    <defs><linearGradient id="opsLoadFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={chartMetric.color} stopOpacity={0.38} /><stop offset="100%" stopColor={chartMetric.color} stopOpacity={0.01} /></linearGradient></defs>
                    <CartesianGrid stroke="rgba(130,160,190,.09)" vertical={false} />
                    <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#5f7184', fontSize: 10 }} minTickGap={42} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#5f7184', fontSize: 10 }} width={46} />
                    <Tooltip contentStyle={{ background: '#0b121a', border: '1px solid rgba(130,160,190,.22)', borderRadius: 8, color: '#edf7ff', fontSize: 11 }} formatter={(value: any) => [`${number(value).toFixed(1)} ${chartMetric.unit}`, chartMetric.label]} />
                    <Area type="monotone" dataKey={chartMetric.key} stroke={chartMetric.color} strokeWidth={2.4} fill="url(#opsLoadFill)" isAnimationActive={false} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <div className="ops-empty"><span>Building history</span><small>Second sample arrives in 15 seconds</small></div>}
            </div>
            <div className="ops-chart-foot"><span>{chartMetric.unit}</span><span>{infra?.historyScope || 'History begins when this cockpit starts observing.'}</span></div>
          </article>

          <article className="ops-panel ops-action-panel">
            <div className="ops-panel-heading"><div><span className="ops-kicker">Decision support</span><h2>{incidents.length ? `${incidents.length} active signals` : 'Action queue clear'}</h2></div><span>ranked by risk</span></div>
            <div className="ops-incident-list">
              {incidents.slice(0, 5).map((incident, index) => (
                <details className={`ops-incident ops-incident-${incident.severity}`} key={incident.id} open={index === 0}>
                  <summary><span>{String(index + 1).padStart(2, '0')}</span><div><strong>{incident.title}</strong><small>{incident.signal}</small></div><i>{incident.severity}</i></summary>
                  <p>{incident.action}</p>
                </details>
              ))}
              {incidents.length === 0 && <div className="ops-empty"><span>No current action signals</span><small>Evidence checks are still live</small></div>}
            </div>
          </article>
        </section>

        <section className="ops-pipeline-strip">
          <div><span>FLOW RATE</span><strong>{totalPerMinute.toFixed(1)}<small>/ min</small></strong></div>
          <div><span>WORST TARGET LOAD</span><strong className={worstCadence.ratio > 1.25 ? 'is-critical' : worstCadence.ratio > 1 ? 'is-warning' : ''}>{worstCadence.ratio.toFixed(2)}×<small>{worstCadence.platform}</small></strong></div>
          <div><span>DATABASE QUEUED</span><strong>{compact(number(queue?.dbQueued ?? health?.jobStates?.queued))}</strong></div>
          <div><span>SQS VISIBLE</span><strong>{compact(number(queue?.available))}</strong></div>
          <div><span>SQS IN FLIGHT</span><strong>{compact(number(queue?.inFlight))}</strong></div>
        </section>

        <section className="ops-panel ops-platform-panel">
          <div className="ops-panel-heading"><div><span className="ops-kicker">Provider lanes</span><h2>Cadence and evidence flow</h2></div><span>rolling 1h · configured objectives</span></div>
          <div className="ops-table-wrap">
            <table className="ops-table">
              <thead><tr><th>State</th><th>Platform</th><th>Objective</th><th>Completed</th><th>Rate</th><th>Queue p95</th><th>Terminal p95</th><th>Failed</th><th>Requests waiting</th></tr></thead>
              <tbody>{platformRows.map((row: any) => (
                <tr key={row.platform_id}>
                  <td><span className={`ops-lane-light ops-lane-${row.severity}`} /></td>
                  <td><strong>{row.platform_id}</strong></td>
                  <td>{duration(number(row.sync_interval_minutes) * 60)}</td>
                  <td>{number(row.completed_1h).toLocaleString()}</td>
                  <td>{row.per_min.toFixed(1)}<small>/m</small></td>
                  <td>{duration(number(row.queue_lag_p95_secs))}</td>
                  <td className={number(row.due_to_terminal_p95_secs) > number(row.sync_interval_minutes) * 60 * 1.25 ? 'ops-value-critical' : number(row.due_to_terminal_p95_secs) > number(row.sync_interval_minutes) * 60 ? 'ops-value-warning' : ''}>{duration(number(row.due_to_terminal_p95_secs))}</td>
                  <td className={number(row.failed_1h) > 0 ? 'ops-value-warning' : ''}>{number(row.failed_1h).toLocaleString()}</td>
                  <td>{number(row.waiting).toLocaleString()}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </section>
      </main>

      <footer className="ops-footer"><span>Evidence order: database → archive proof → queue → logs</span><nav><Link to="/health">Deep health</Link><Link to="/jobs">Jobs</Link><Link to="/targets">Targets</Link><button onClick={logout}>Lock</button></nav></footer>

      {agentOpen && <div className="ops-drawer-backdrop" onClick={() => setAgentOpen(false)}><aside className="ops-drawer" onClick={(event) => event.stopPropagation()} aria-label="Agent diagnostics brief"><div className="ops-drawer-header"><div><span className="ops-kicker">Machine-readable</span><h2>Agent diagnostics brief</h2></div><button onClick={() => setAgentOpen(false)} aria-label="Close">×</button></div><p>A prioritized, evidence-backed starting point with infrastructure snapshots and explicit action guardrails.</p><div className="ops-drawer-actions"><button onClick={copyBrief}>{copied ? 'Copied' : 'Copy JSON'}</button><span>schema ops-diagnostics.v2</span></div><pre data-testid="agent-diagnostics-json">{JSON.stringify(agentBrief, null, 2)}</pre></aside></div>}
    </div>
  );
}
